/* eslint-disable react/no-unstable-nested-components */
import { Mutation } from "@apollo/client/react/components";
import RatingStars from "components/rating/RatingStars";
import TotalRatingsLine from "components/rating/TotalRatingsLine";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import differenceInMonths from "date-fns/differenceInMonths";
import parseISO from "date-fns/parseISO";
import RankedIssuerFields from "lib/fragments/RankedIssuerFields";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import type { IRankedIssuerFieldsFragment } from "lib/fragments/__generated__/RankedIssuerFields.generated";
import useSelfie from "lib/hooks/useSelfie";
import RateIssuer from "lib/queries/RateIssuer";
import type { IRateIssuerMutation, IRateIssuerMutationVariables } from "lib/queries/__generated__/RateIssuer.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import { BellRingIcon } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import type { IBrazilianRealEstateFundIssuer } from "types/graphqlTypes";
import { IRankingIssuerType } from "types/graphqlTypes";

interface IRatingProps {
  issuer:
    | IBrazilianCompanyIssuerTopLevelFragment
    | IBrazilianRealEstateFundTopLevelFragment
    | IBrazilianRealEstateFundIssuer
    | IAmericanCompanyIssuerTopLevelFragment;
  rankingPlacement: IRankedIssuerFieldsFragment["overallRankingPlacement"];
  rankingRatingSummary: IRankedIssuerFieldsFragment["overallRankingRatingSummary"];
}

function Rating({ issuer, rankingPlacement, rankingRatingSummary }: IRatingProps) {
  const intl = useIntl();
  const selfie = useSelfie();

  const overallRating = rankingRatingSummary ? rankingRatingSummary.overallRating : 0;
  const percentage = (overallRating / 5) * 100;
  const needsToRenewRating = issuer.rating && differenceInMonths(new Date(), parseISO(issuer.rating.ratedAt)) >= 3;

  return (
    <Mutation<IRateIssuerMutation, IRateIssuerMutationVariables> mutation={RateIssuer}>
      {(rateIssuer, { error, loading }) => (
        <div className="space-y-2">
          <div className="flex items-center justify-center gap-3 md:justify-start">
            {overallRating > 0 && (
              <div
                className="h-14 w-14 rounded-[50%]"
                style={{
                  background: `radial-gradient(closest-side, white 85%, transparent 90% 100%), conic-gradient(#f59e0b ${percentage}%, #f5f5f5 0)`,
                }}
                role="progressbar"
                aria-valuenow={percentage}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <span
                  className={classNames(
                    "flex h-full w-full select-none items-center justify-center",
                    "font-aeonik font-bold text-amber-500 md:text-xl",
                  )}
                >
                  {overallRating}
                </span>
              </div>
            )}

            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-center space-x-2 md:justify-start">
                <RatingStars
                  rating={needsToRenewRating ? 0 : issuer.rating?.value || 0}
                  isSubmittingRating={loading}
                  onSubmitRating={(rating) => {
                    if (issuer.__typename === "BrazilianRealEstateFundIssuer") {
                      return;
                    }

                    let issuerType = IRankingIssuerType.AmericanCompany;

                    if (issuer.__typename === "BrazilianCompanyIssuer") {
                      issuerType = IRankingIssuerType.BrazilianCompany;
                    }
                    if (issuer.__typename === "BrazilianRealEstateFund") {
                      issuerType = IRankingIssuerType.BrazilianRealEstateFund;
                    }

                    if (rating === issuer.rating?.value && !needsToRenewRating) {
                      rateIssuer({
                        variables: {
                          input: {
                            issuerId: issuer._id,
                            issuerType,
                            rating: 0,
                          },
                        },
                        update: (cache) => {
                          const data = cache.readFragment<IRankedIssuerFieldsFragment>({
                            fragment: RankedIssuerFields,
                            id: cache.identify(issuer),
                          });
                          if (!data) {
                            return;
                          }
                          cache.writeFragment({
                            fragment: RankedIssuerFields,
                            id: cache.identify(issuer),
                            data: {
                              ...data,
                              rating: null,
                            },
                          });
                        },
                      });
                    }

                    if (rating === issuer.rating?.value && !needsToRenewRating) {
                      return;
                    }

                    rateIssuer({
                      variables: {
                        input: {
                          issuerId: issuer._id,
                          issuerType,
                          rating,
                        },
                      },
                      update: (cache) => {
                        const data = cache.readFragment<IRankedIssuerFieldsFragment>({
                          fragment: RankedIssuerFields,
                          id: cache.identify(issuer),
                        });
                        if (!data) {
                          return;
                        }
                        cache.writeFragment({
                          fragment: RankedIssuerFields,
                          id: cache.identify(issuer),
                          data: {
                            ...data,
                            rating: {
                              ...data.rating,
                              value: rating,
                              ratedAt: new Date().toISOString(),
                            },
                          },
                        });
                      },
                    });
                  }}
                />

                <div className="flex items-center justify-center space-x-3 md:justify-start">
                  {issuer.rating && needsToRenewRating && (
                    <SimpleTextTooltip
                      content={
                        <FormattedMessage
                          defaultMessage="Sua última avaliação foi em <strong>{date}</strong>"
                          id="yOvLuf"
                          values={{
                            strong: (text) => <strong>{text}</strong>,
                            date: intl.formatDate(parseISO(issuer.rating.ratedAt), {
                              year: "numeric",
                              month: "numeric",
                              day: "numeric",
                            }),
                          }}
                        />
                      }
                    >
                      <div className="inline-flex items-center justify-center space-x-2 rounded-md bg-orange-50 p-1 px-2 text-orange-500">
                        <BellRingIcon className="h-4 w-4" strokeWidth={2} />
                      </div>
                    </SimpleTextTooltip>
                  )}
                </div>
              </div>

              {rankingRatingSummary && (
                <TotalRatingsLine
                  totalRatings={rankingRatingSummary.totalRatings}
                  periodStart={parseDateYmd(rankingRatingSummary.periodStartDate)}
                  periodEnd={parseDateYmd(rankingRatingSummary.periodEndDate)}
                />
              )}
            </div>

            {rankingPlacement && rankingPlacement.position > 0 && selfie && selfie.isPremium && (
              <strong className="hidden h-fit rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-gray-700 md:flex">
                <FormattedMessage
                  defaultMessage="{position}º no ranking"
                  id="T5qQ9X"
                  values={{
                    position: rankingPlacement.position,
                  }}
                />
              </strong>
            )}
          </div>

          {issuer.rating && needsToRenewRating && selfie?.isPremium && (
            <div>
              <p className="my-2 inline-flex w-full flex-col items-center gap-2 rounded-xl bg-yellow-600/5 p-3 text-left text-sm font-medium text-yellow-600 md:flex-row md:p-2">
                <BellRingIcon className="size-6 lg:size-4" strokeWidth={2} />
                <span>
                  <FormattedMessage
                    defaultMessage="Sua última avaliação foi em <strong>{date}</strong>. Renove sua nota para mantermos o ranking atualizado!"
                    id="7PkVno"
                    values={{
                      strong: (text) => <strong>{text}</strong>,
                      date: intl.formatDate(parseISO(issuer.rating.ratedAt), {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                      }),
                    }}
                  />
                </span>
              </p>
            </div>
          )}

          {error && (
            <div>
              <div
                className={classNames(
                  "inline-flex w-full justify-center rounded-xl bg-rose-50 px-3 py-2 text-center",
                  "text-sm font-normal text-red-600 md:w-auto md:max-w-xs md:justify-start",
                )}
              >
                <FormattedMessage defaultMessage="Não foi possível enviar sua nota..." id="TBsyTo" />
              </div>
            </div>
          )}
        </div>
      )}
    </Mutation>
  );
}

export default Rating;
